/**
 * Colour-scheme control.
 *
 * A three-way menu rather than a two-state switch, because "follow the system" is a real preference that a boolean
 * cannot represent — and it is the default, so hiding it would strand users in whichever scheme their OS happened to
 * be in when they first loaded the app.
 */

import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useTheme } from '@/hooks/useTheme';
import { THEME_PREFERENCES } from '@/types/api';
import type { ThemePreference } from '@/types/api';

/** Props accepted by {@link ThemeToggle}. */
export interface ThemeToggleProps {
  /** Extra classes for the trigger. */
  className?: string;
}

/** Translation key of each preference, plus the icon shown next to it. */
const OPTIONS: Record<ThemePreference, { labelKey: string; icon: typeof Sun }> = {
  System: { labelKey: 'common.system', icon: Monitor },
  Light: { labelKey: 'common.light', icon: Sun },
  Dark: { labelKey: 'common.dark', icon: Moon },
};

/** Lets the user choose between the system scheme, light and dark. */
export function ThemeToggle({ className }: ThemeToggleProps) {
  const { t } = useTranslation();
  const { preference, resolved, setPreference } = useTheme();
  const CurrentIcon = resolved === 'dark' ? Moon : Sun;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={className}
          aria-label={`${t('common.theme')} — ${t(OPTIONS[preference].labelKey)}`}
        >
          <CurrentIcon aria-hidden className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{t('common.theme')}</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(value) => setPreference(value as ThemePreference)}
        >
          {THEME_PREFERENCES.map((option) => {
            const Icon = OPTIONS[option].icon;

            return (
              <DropdownMenuRadioItem key={option} value={option}>
                <Icon aria-hidden className="h-4 w-4" />
                {t(OPTIONS[option].labelKey)}
              </DropdownMenuRadioItem>
            );
          })}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
